import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import Header from '@/components/ui/Header';
import StatCard from './components/StatCard';
import ActivityFeed from './components/ActivityFeed';
import QuickActions from './components/QuickActions';
import ProgressChart from './components/ProgressChart';
import SubscriptionStatus from './components/SubscriptionStatus';
import {LoadingState} from './components/LoadingState';
import apiRequest from "@/services/requests";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
 
const Dashboard = () => {
const navigate = useNavigate();
const { user } = useUser();
const [statsData, setStats] = useState(null);
const [chart, setChart] = useState([]);
const [recent, setRecent] = useState([]);


useEffect(() => { if (user) fetchDashboard(); }, [user]);



const fetchDashboard = async () => {
try{
// Check localStorage for saved language preference
const savedLanguage = localStorage.getItem('currentLanguage') || 'en';

const data  = await apiRequest.getStats();
setChart(data.weeklyTrend);
setStats([
{
title: 'Total Applications',
value: data.currentMonthApplications,
subtitle: 'This month',
icon: 'Send',
trend: 'up',
trendValue: '+0%',
color: 'primary',
onClick: () => handleStatCardClick('/applications')
},
{
title: 'Job Matches',
value: data.pendingMatches,
subtitle: 'Pending',
icon: 'Target',
trend: 'up',
trendValue: '+0%',
color: 'success',
onClick: () => handleStatCardClick('/job-matching')
},
{
title: 'Interviews Scheduled',
value: data.nextTwoWeeksInterviews,
subtitle: 'Next 2 weeks',
icon: 'Calendar',
trend: 'up',
trendValue: '+0',
color: 'warning',
onClick: () => handleStatCardClick('/interviews')
}
]);

/* reuse the same notifications endpoint for activity feed */
const notif = await apiRequest.notificationHeader();

setRecent(
notif.list.map(n => {
/* exact label → activity type */
const typeMap = {
'Application Submitted': 'application',
'Interview Scheduled':   'interview',
'New Job Match':         'match',
'Status Update':         'notification'
};

const colorMap = {
application:  'success',
match:        'primary',
interview:    'warning',
notification: 'success'
};

const iconMap = {
application:  'Send',
match:        'Target',
interview:    'Calendar',
notification: 'CheckCircle'
};

const type = typeMap[n.type];

return {
id:          n.id,
type:        type,
title:       n.type,
description: `${n.title} at ${n.company}`,
timestamp:   dayjs(n.when).fromNow(),
icon:        iconMap[type],
color:       colorMap[type]
};
}).slice(0, 6)
);

} catch (e) {
console.error('Dashboard fetch failed', e);
} finally{

window.dispatchEvent(
new CustomEvent('openSystemUpgradeModal', {
detail: {
message: 'Scheduled maintenance tonight at 23:00 UTC.'
}
})
);


}
};




const handleStatCardClick = (route) => {
if (route) {
navigate(route);
}
};




if (!statsData) return (
<div className="min-h-screen bg-background">
<Header />
<main className="pt-16">
<div className="max-w-7xl mx-auto px-6 py-8">
<LoadingState />
</div>
</main>
</div>);



return (
<div className="min-h-screen bg-background">
<Header />
<main className="pt-16">
<div className="max-w-7xl mx-auto px-6 py-8">
{/* Welcome Section */}
<div className="mb-8">
<h1 className="text-xl lg:text-3xl font-bold text-text-primary mb-2">
Welcome back, {user?.personal.firstName}! 👋
</h1>
<p className="text-text-secondary text-sm">
Here's your job search progress and latest updates.
</p>
</div>

{/* Statistics Cards */}
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
{statsData?.map((stat, index) => (
<StatCard
key={index}
title={stat?.title}
value={stat?.value}
subtitle={stat?.subtitle}
icon={stat?.icon}
trend={stat?.trend}
trendValue={stat?.trendValue}
color={stat?.color}
onClick={stat?.onClick}
/>
))}
</div>

{/* Main Content Grid */}
<div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
{/* Left Column - Chart */}
<div className="lg:col-span-2">
<ProgressChart weeklyData={chart} />
</div>

{/* Right Column - Quick Actions */}
<div>
<QuickActions />
</div>
</div>

{/* Bottom Section */}
<div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
{/* Activity Feed */}
<div>
<ActivityFeed activities={recent} navigate={navigate} />
</div>

{/* Subscription Status */}
<div>
<SubscriptionStatus profile={user.package} navigate={navigate} />
</div>
</div>

{/* Mobile-specific Quick Access */}
<div className="hidden mt-8">
<div className="bg-card border border-border rounded-lg p-6">
<h3 className="text-base sm:text-lg font-semibold text-text-primary mb-4">
Quick Navigation
</h3>
<div className="grid grid-cols-2 gap-4">
<button
onClick={() => navigate('/job-matching')}
className="flex flex-col items-center p-4 bg-primary/10 text-primary rounded-lg spring-transition hover:bg-primary/20"
>
<div className="w-8 h-8 mb-2">
<svg viewBox="0 0 24 24" fill="currentColor">
<path d="M20 6h-2V4c0-1.11-.89-2-2-2H8c-1.11 0-2 .89-2 2v2H4c-1.11 0-2 .89-2 2v11c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zM8 4h8v2H8V4zm12 15H4V8h16v11z"/>
</svg>
</div>
<span className="text-sm font-medium">Jobs</span>
</button>
<button
onClick={() => navigate('/career-assessment')}
className="flex flex-col items-center p-4 bg-success/10 text-success rounded-lg spring-transition hover:bg-success/20"
>
<div className="w-8 h-8 mb-2">
<svg viewBox="0 0 24 24" fill="currentColor">
<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
</svg>
</div>
<span className="text-sm font-medium">Assessment</span>
</button>
</div>
</div>
</div>
</div>
</main>
</div>
);
};

export default Dashboard;